import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { MachineExtended } from "../../CustomComponents/MachineExtended";

type IWin = Communication.Game.IWin;
var GameModules = Communication.Game.GameModules;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    static INTRO_DELAY: number = 60;
    static OUTRO_DELAY: number = 60;
    static OUTRO_DELAY_TO_GAMBLE: number = 30;
    static OUTRO_DELAY_TO_GAMBLE_IN_HIGHWINS: number = 60;
    static INTRO_DELAY_FROM_GAMBLE: number = 30;
    static OUTRO_DELAY_TO_FREESPINS: number = 90;
    static INTRO_DELAY_FROM_FREESPINS: number = 30;
    static OUTRO_DELAY_IN_HIGHWINS: number = 90;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;

    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;
    private _groupTweens: SwitchElement;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.baseStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "GroupTweens" && this.childs[key] instanceof SwitchElement) {
                    this._groupTweens = this.childs[key]; // SwitchElement with 2 elements of GroupTweens
                }
            }

            // set childs to GroupTweens object
            if (this._groupTweens) {
                let groupTweenElements: Object = this._groupTweens.getChildElements();
                if (groupTweenElements) {
                    for (let key in groupTweenElements) {
                        if (groupTweenElements.hasOwnProperty(key)) {
                            if (groupTweenElements[key] instanceof GroupTweens) {
                                (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                            }
                        }
                    }
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                            this.childs["NoTrimContainer"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                null,
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (ModuleManager.gambleActive()) {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleInit);
            }
        }

        this.toggleElements(false);
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        // this.allSwitchElementToCase("INIT_INTRO");

        if (GameManager.gameResponse.totalWin > 0) {
            if (GameManager.baseResponse.currentWin == 0) {
                //init state after fs
                PlayList.loop("SND_BaseRecapLoop");
            }
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);

        this.toggleElements(true);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop3");
        PlayList.stop("SND_BaseRecapLoop4");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop3");
        PlayList.stop("SND_BaseRecapLoop4");
        PlayList.loop("SND_BaseLoop");

        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            PlayList.stop("SND_BaseLoop");
            // PlayList.loop('SND_BaseRecapLoop');
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false, previousModule: string): void {
        this.toggleElements(true, true);

        StageController.baseStage = true;

        if (GameManager.gameResponse.totalWin > 0) {
            //fs return
            //PlayList.stop('SND_BaseLoop');
            PlayList.loop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop3");
            PlayList.stop("SND_BaseRecapLoop4");
            PlayList.loop("SND_BaseLoop");
        }

        let delayIntro: number = BaseView.INTRO_DELAY;
        if (previousModule && this._groupTweens) {
            if (previousModule === GameModules.Gamble) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleToBase);
                delayIntro = BaseView.INTRO_DELAY_FROM_GAMBLE;
            } else if (previousModule === GameModules.Freespins) {
                this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBase);
                delayIntro = BaseView.INTRO_DELAY_FROM_FREESPINS;
            } else {
                this._groupTweens.goToCase(GroupTweens.CaseIntro);
            }
        }

        this._introDelay.start(delayIntro);
    }

    introFinished(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;
        this._controller.introFinished();
    }

    outro(): void {
        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop3");
        PlayList.stop("SND_BaseRecapLoop4");

        if (ModuleManager.gambleActive()) {
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
            }

            if (inHighWins && !wasFinished) {
                this._outroDelay.start(BaseView.OUTRO_DELAY_TO_GAMBLE_IN_HIGHWINS);
            } else {
                this._outroDelay.start(BaseView.OUTRO_DELAY_TO_GAMBLE);
            }
        } else {
            if (wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(BaseView.OUTRO_DELAY_IN_HIGHWINS);
                } else {
                    this._outroDelay.start(BaseView.OUTRO_DELAY);
                }
            }
        }
    }

    outroFinished(): void {
        this.toggleElements(false, true);
        this._outroDelay.stop();

        this._controller.outroFinished();
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop3");
            PlayList.stop("SND_BaseRecaploop4");
        }
    }

    toggleElements(visible: boolean, long: boolean = false): void {
        if (long) {
            let alphas: number[] = [1, 0];
            if (visible) {
                alphas = [0, 1];
            }

            if (this.childs["Foreground"]) {
                this.childs["Foreground"].goToCase(visible ? "NORMAL" : "GAMBLE");
            }
            //
            //     if (this.childs['Crosses']) {
            //         Tweens.removeTweens(this.childs['Crosses'], 'all');
            //
            //         let options : any = {
            //             alphas: alphas.slice(0),
            //             tweenDuration: 15,
            //         };
            //         if (!visible) {
            //             options.callbackType = "onComplete";
            //             options.callbackFunction = () => {
            //                 this.childs['Crosses'].visible = false;
            //             };
            //         } else {
            //             this.childs['Crosses'].visible = true;
            //         }
            //
            //         Tweens.alphaTween(this.childs['Crosses'], options);
            //     }
            //
            if (this.childs["Background"]) {
                this.childs["Background"].goToCase(visible ? "NORMAL" : "GAMBLE");
            }

            if (this.childs["WinAnimator"]) {
                Tweens.removeTweens(this.childs["WinAnimator"], "all");

                let options: any = {
                    alphas: alphas.slice(0),
                    tweenDuration: 15,
                };
                if (!visible) {
                    options.callbackType = "onComplete";
                    options.callbackFunction = () => {
                        this.childs["WinAnimator"].visible = false;
                    };
                } else {
                    this.childs["WinAnimator"].visible = true;
                }
                Tweens.alphaTween(this.childs["WinAnimator"], options);
            }

            if (this.childs["NoWinContainer"]) {
                Tweens.removeTweens(this.childs["NoWinContainer"], "all");

                let options: any = {
                    alphas: alphas.slice(0),
                    tweenDuration: 15,
                };
                if (!visible) {
                    options.callbackType = "onComplete";
                    options.callbackFunction = () => {
                        this.childs["NoWinContainer"].visible = false;
                    };
                } else {
                    this.childs["NoWinContainer"].visible = true;
                }
                Tweens.alphaTween(this.childs["NoWinContainer"], options);
            }

            /*
            if (this.childs['Logo']) {
                Tweens.removeTweens(this.childs['Logo'], 'all');

                let options : any = {
                    alphas: alphas.slice(0),
                    tweenDuration: 15,
                };
                if (!visible) {
                    options.callbackType = "onComplete";
                    options.callbackFunction = () => {
                        this.childs['Logo'].visible = false;
                    };
                } else {
                    this.childs['Logo'].visible = true;
                }
                Tweens.alphaTween(this.childs['Logo'], options);
            }
            */
        } else {
            if (this.childs["Foreground"]) {
                this.childs["Foreground"].goToCase(visible ? "NORMAL" : "GAMBLE_FAST");
            }

            //
            //     if (this.childs['Crosses']) {
            //         this.childs['Crosses'].alpha = visible ? 1 : 0;
            //         this.childs['Crosses'].visible = visible;
            //     }
            //
            if (this.childs["Background"]) {
                this.childs["Background"].goToCase(visible ? "NORMAL" : "GAMBLE_FAST");
            }

            if (this.childs["WinAnimator"]) {
                this.childs["WinAnimator"].alpha = visible ? 1 : 0;
                this.childs["WinAnimator"].visible = visible;
            }

            if (this.childs["NoWinContainer"]) {
                this.childs["NoWinContainer"].alpha = visible ? 1 : 0;
                this.childs["NoWinContainer"].visible = visible;
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/